import { BaseApiClient } from './base-client';

export interface BackupSchedule {
  id: number;
  service_id: number;
  name: string;
  cron_expression: string;
  retention_policy: RetentionPolicy;
  enabled: boolean;
  created_at: string;
  updated_at?: string;
  last_run_at?: string;
  next_run_at?: string;
  last_backup_at?: string;
  next_backup_at?: string;
  total_size_bytes?: number;
  client_connected?: boolean;
}

export interface RetentionPolicy {
  full_backups: number;
  incremental_backups: number;
  full_backup_interval_days: number;
}

export interface BackupSession {
  id: number;
  schedule_id: number;
  machine_id: string;
  backup_type: 'full' | 'incremental';
  started_at: string;
  completed_at?: string;
  status: 'running' | 'completed' | 'failed';
  size_bytes?: number;
  files_count?: number;
  error_message?: string;
}

export interface BackupFile {
  name: string;
  size: number;
  mtime: string;
}

export interface BackupBrowseResponse {
  files: BackupFile[];
  directories: string[];
}

export interface BackupUsage {
  total_size_bytes: number;
  total_backups: number;
  full_backups: number;
  incremental_backups: number;
  oldest_backup?: string;
  newest_backup?: string;
  estimated_monthly_cost: number;
}

export interface BackupClient {
  id: number;
  service_id: number;
  machine_id: string;
  machine_name: string;
  platform: string;
  ssh_public_key?: string;
  ssh_port: number;
  vpn_hostname?: string;
  public_hostname?: string;
  last_seen_at: string;
  vpn_capable: boolean;
  client_metadata?: any;
}

export interface CreateScheduleRequest {
  name: string;
  cron_expression?: string;
  retention_policy?: Partial<RetentionPolicy>;
  source_paths?: string[];
  enabled?: boolean;
}

export interface UpdateScheduleRequest {
  name?: string;
  cron_expression?: string;
  retention_policy?: Partial<RetentionPolicy>;
  source_paths?: string[];
  enabled?: boolean;
}

export interface RestoreRequest {
  backup_id: number;
  restore_path?: string;
  files?: string[];
}

export class BackupsApiClient extends BaseApiClient {

  // Session operations
  async listSessions(serviceId: number, scheduleId?: number, limit?: number): Promise<BackupSession[]> {
    try {
      const params = new URLSearchParams();
      if (scheduleId) params.append('schedule_id', scheduleId.toString());
      if (limit) params.append('limit', limit.toString());

      return await this.get<BackupSession[]>(`/api/v1/backups/${serviceId}/sessions?${params.toString()}`);
    } catch (error: any) {
      console.error('Failed to list sessions:', error);
      throw error.response?.data || error;
    }
  }

  async browseSession(serviceId: number, sessionId: number, path?: string): Promise<BackupBrowseResponse> {
    try {
      const params = path ? `?path=${encodeURIComponent(path)}` : '';
      return await this.get<BackupBrowseResponse>(`/api/v1/backups/${serviceId}/sessions/${sessionId}/browse${params}`);
    } catch (error: any) {
      console.error('Failed to browse session:', error);
      throw error.response?.data || error;
    }
  }

  // Browse backup
  async browseBackup(serviceId: number, backupId: number, path: string = '/'): Promise<BackupBrowseResponse> {
    try {
      const params = new URLSearchParams();
      params.append('path', path);
      return await this.get<BackupBrowseResponse>(`/api/v1/backups/${serviceId}/sessions/${backupId}/browse?${params.toString()}`);
    } catch (error: any) {
      console.error('Failed to browse backup:', error);
      throw error.response?.data || error;
    }
  }

  async registerClient(serviceId: number, data: any): Promise<BackupClient> {
    try {
      return await this.post<BackupClient>(`/api/v1/backups/${serviceId}/clients`, data);
    } catch (error: any) {
      console.error('Failed to register client:', error);
      throw error.response?.data || error;
    }
  }

  // VPN config
  async getVpnConfig(serviceId: number): Promise<{ vpn_config: string; vpn_hostname: string; public_hostname: string }> {
    try {
      return await this.get<{ vpn_config: string; vpn_hostname: string; public_hostname: string }>(`/api/v1/backups/${serviceId}/vpn-config`);
    } catch (error: any) {
      console.error('Failed to get VPN config:', error);
      throw error.response?.data || error;
    }
  }

  // Schedule operations
  async listSchedules(serviceId: number): Promise<BackupSchedule[]> {
    try {
      return await this.get<BackupSchedule[]>(`/api/v1/backups/${serviceId}/schedules`);
    } catch (error: any) {
      console.error('Failed to list schedules:', error);
      throw error.response?.data || error;
    }
  }

  async createSchedule(serviceId: number, data: CreateScheduleRequest): Promise<BackupSchedule> {
    try {
      return await this.post<BackupSchedule>(`/api/v1/backups/${serviceId}/schedules`, data);
    } catch (error: any) {
      console.error('Failed to create schedule:', error);
      throw error.response?.data || error;
    }
  }

  async updateSchedule(serviceId: number, scheduleId: number, data: UpdateScheduleRequest): Promise<BackupSchedule> {
    try {
      return await this.put<BackupSchedule>(`/api/v1/backups/${serviceId}/schedules/${scheduleId}`, data);
    } catch (error: any) {
      console.error('Failed to update schedule:', error);
      throw error.response?.data || error;
    }
  }

  async deleteSchedule(serviceId: number, scheduleId: number): Promise<void> {
    try {
      await this.delete(`/api/v1/backups/${serviceId}/schedules/${scheduleId}`);
    } catch (error: any) {
      console.error('Failed to delete schedule:', error);
      throw error.response?.data || error;
    }
  }

  async triggerBackup(serviceId: number, scheduleId: number): Promise<void> {
    try {
      await this.post(`/api/v1/backups/${serviceId}/schedules/${scheduleId}/trigger`, {});
    } catch (error: any) {
      console.error('Failed to trigger backup:', error);
      throw error.response?.data || error;
    }
  }

  // Usage
  async getUsage(serviceId: number): Promise<BackupUsage> {
    try {
      return await this.get<BackupUsage>(`/api/v1/backups/${serviceId}/usage`);
    } catch (error: any) {
      console.error('Failed to get backup usage:', error);
      throw error.response?.data || error;
    }
  }

  // Clients
  async listClients(serviceId: number): Promise<BackupClient[]> {
    try {
      return await this.get<BackupClient[]>(`/api/v1/backups/${serviceId}/clients`);
    } catch (error: any) {
      console.error('Failed to list backup clients:', error);
      throw error.response?.data || error;
    }
  }
}